<template>
  <div class="program-form-page">
    <el-breadcrumb separator-class="el-icon-arrow-right">
      <el-breadcrumb-item :to="{ path: '/' }">
        <app-i18n code="home.menu"></app-i18n>
      </el-breadcrumb-item>
      <el-breadcrumb-item :to="{ path: '/program' }">
        <app-i18n code="entities.program.menu"></app-i18n>
      </el-breadcrumb-item>
      <el-breadcrumb-item>
        <app-i18n code="entities.program.edit.title" v-if="isEditing"></app-i18n>
        <app-i18n code="entities.program.new.title" v-if="!isEditing"></app-i18n>
      </el-breadcrumb-item>
    </el-breadcrumb>
    <!-- Back button -->
    <app-back-button
      v-if="type"
      route-name="programList"
      :route-params="{ type: type }"
      :i18nCode="!isPropertyNBA ? `enumerators.programType.${type}` : 'entities.season.label'" 
    />
    <app-back-button
      v-else
      route-name="programList"
      :route-params="{ type: type }"
      i18nCode="entities.program.label.many" 
    />

    <!-- Title -->
    <h1 class="app-content-title">
      <strong>
        <app-i18n v-if="isEditing && !type" code="entities.program.edit.title" />
        <app-i18n v-else-if="!isEditing && !type" code="entities.program.new.title" />
      </strong>
      <span>&nbsp;</span>
        <app-i18n v-if="isEditing && type" :code="!isPropertyNBA ? `entities.program.edit.programType.${type}` : 'entities.season.edit.title'" />
        <app-i18n v-else-if="!isEditing && type" :code="!isPropertyNBA ? `entities.program.new.programType.${type}` : 'entities.season.new.title'" />
    </h1>

    <div class="app-content-page">
      <div class="app-page-spinner" v-if="findLoading" v-loading="findLoading"></div>
      <el-form
        v-loading="saveLoading"
        :label-position="labelPosition"
        :label-width="labelWidthForm"
        :model="model"
        :rules="rules"
        @submit.native.prevent="doSubmit"
        class="form"
        ref="form"
        v-if="model"
      >

        <!-- Information Subtitle -->
        <h2 class="app-content-subtitle">
          <app-i18n code="entities.program.form.subtitle.information" />
        </h2>

        <el-row :gutter="10">
          <!-- id -->
          <el-col :lg="8" :md="12" :sm="24"
            v-if="hasPermissionToAuditLogs"
          >
            <el-form-item :label="fields.id.label" :prop="fields.id.name" v-if="isEditing">
                <el-input :disabled="true" v-model="model[fields.id.name]" />
            </el-form-item>
          </el-col>

          <!-- type -->
          <el-col :lg="8" :md="12" :sm="24">
            <el-form-item
              :label="!isPropertyNBA ? fields.type.label : i18n('entities.season.fields.type')"
              :prop="fields.type.name"
              :required="fields.type.required"
            >
                <el-select 
                  :disabled="disabledType"
                  :placeholder="fields.type.label" 
                  v-model="model[fields.type.name]"
                >
                  <i
                    slot="prefix"
                    class="icon-cup"
                  />
                  <el-option
                    :key="option.id"
                    :label="option.label"
                    :value="option.id"
                    v-for="option in programTypeOptions"
                  ></el-option>
                </el-select>
            </el-form-item>
          </el-col>

          <!-- name -->
          <el-col :lg="8" :md="12" :sm="24">
            <el-form-item
              :label="!isPropertyNBA ? fieldNameLabel : i18n('entities.season.fields.name')"
              :prop="fields.name.name"
              :required="fields.name.required"
            >

                <el-input
                  :placeholder="!isPropertyNBA ? fieldNamePlaceholder : i18n('entities.season.fields.name')"
                  v-model="model[fields.name.name]"
                >
                  <i
                    slot="prefix"
                    class="icon-cup"
                  />
                </el-input>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- Form controls -->
        <el-form-item>
            <div class="form-buttons">
                <router-link :to="{name: 'programList', params: {type}}">
                    <el-button type="secondary">
                        <app-i18n code="common.back.default" />
                    </el-button>
                </router-link>
                <el-button
                    class="right"
                    :disabled="saveLoading"
                    @click="doSubmit"
                    type="primary"
                >
                    <app-i18n v-if="!isEditing" code="common.create"/>
                    <app-i18n v-if="isEditing" code="common.save" />
                </el-button>

            </div>
        </el-form-item>
      </el-form>
    </div>
  </div>
</template>

<script>
import { AuditLogPermissions } from '@modules/audit-log/audit-log-permissions';
import { mapGetters, mapActions } from 'vuex';
import { FormSchema } from '@shared/form/form-schema';
import { ProgramModel } from '@modules/program/program-model';
import { i18n } from '@shared/i18n';
import { getProgramTypeListByPropertyType } from '@shared/factories/enums-factory';
import { get } from 'lodash';

const { fields } = ProgramModel;
const formSchema = new FormSchema([
  fields.id,
  fields.name,
  fields.type,
]);

export default {
  name: 'app-program-form-page',

  props: {
    id: {
      type: String,
    },
    manage: {
      type: Object,
    },
    type: {
      type: String,
    },
  },

  data() {
    return {
      rules: formSchema.rules(),
      model: null,
    };
  },

  computed: {
    ...mapGetters({
      labelPosition: 'layout/labelPosition',
      labelWidthForm: 'layout/labelWidthForm',
      record: 'program/form/record',
      findLoading: 'program/form/findLoading',
      saveLoading: 'program/form/saveLoading',
      currentManage: 'auth/manage/currentManage',
      isPropertyNBA: 'auth/manage/isPropertyNBA',
    }),
    hasPermissionToAuditLogs() {
      return new AuditLogPermissions(this.currentUser).read;
    },

    isEditing() {
      return !!this.id;
    },

    fields() {
      return fields;
    },

    manageToProgram() {
      if (this.manage) {
        return this.manage;
      } else {
        return this.currentManage;
      }
    },

    disabledType() {
      return get(this, 'model.type', null) === 'league';
    },

    programTypeOptions() {
      const propertyType = get(this, 'currentManage.property.type', null);
      const programs = getProgramTypeListByPropertyType(propertyType);
      
      return fields.type.options.filter(({ id }) => {
        return programs.some(
          (programId) => programId === id,
        );
      });
    },

    fieldNameLabel() {
      if (this.model.type == 'club') {
        return i18n('entities.program.fields.season');
      }
      return i18n('entities.program.fields.name');
    },

    fieldNamePlaceholder() {
      if (this.model.type == 'club') {
        return i18n('entities.program.placeholders.season');
      }
      return i18n('entities.program.placeholders.name');
    },

    title() {
      if (this.isEditing) {
        return (
          i18n(`common.edit`) +
          ' ' +
          i18n(`enumerators.programType.${this.type}`)
        );
      } else {
        return (
          i18n(`common.new`) +
          ' ' +
          i18n(`enumerators.programType.${this.type}`)
        );
      }
    },
  },

  async created() {
    if (this.isEditing) {
      await this.doFind({
        id: this.id,
        manage: this.manageToProgram,
      });
      this.model = formSchema.initialValues(this.record);
    } else {
      await this.doNew();
      this.model = formSchema.initialValues({
        ...this.record,
        type: this.type,
      });
    }
  },

  methods: {
    ...mapActions({
      doFind: 'program/form/doFind',
      doNew: 'program/form/doNew',
      doUpdate: 'program/form/doUpdate',
      doCreate: 'program/form/doCreate',
    }),

    doReset() {
      this.model = formSchema.initialValues(this.record);
    },

    async doSubmit() {
      try {
        await this.$refs.form.validate();
      } catch (error) {
        return;
      }

      const { id, ...values } = formSchema.cast(this.model);

      if (this.isEditing) {
        return this.doUpdate({
          id,
          values,
          manage: this.manageToProgram,
        });
      } else {
        return this.doCreate({
          values,
          manage: this.manageToProgram,
        });
      }
    },
    i18n(code) {
      return i18n(code);
    },
  },
};
</script>

<style scoped>

</style>
